import React, { useEffect, useMemo, useRef, useState } from "react";
import { HiOutlinePlus, HiOutlineChevronRight, HiOutlineRefresh, HiOutlineCloudDownload, HiOutlineExclamation, HiOutlineSparkles, HiOutlineTrash } from "react-icons/hi";
import { PiClockCounterClockwise, PiCheckBold } from "react-icons/pi";
import { LuNetwork } from "react-icons/lu";
import { useHistory } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-hot-toast";

import API from "../../api";
import { capture } from "../../instrument";
import { useVirtualizer } from "@tanstack/react-virtual";
import { useHotkeys } from "react-hotkeys-hook";
import useListShortcuts from "../../hooks/useListShortcuts";
import { SHORTCUTS } from "../../utils/shortcuts";
import { notifyShortcut } from "../../utils/shortcutTip";
import { rankBySearch } from "../../utils/search";
import useSelection from "../../hooks/useSelection";
import BulkActionBar from "../../components/selection/BulkActionBar";
import useSavedViews from "../../hooks/useSavedViews";
import useSearchFilters, { SEARCH_OPERATOR } from "../../hooks/useSearchFilters";
import SearchFilters from "../../components/SearchFilters";
import SaveViewModal from "../../components/SaveViewModal";
import ViewTabs from "../../components/ViewTabs";
import {
  summarizeGuidelineTriggers,
  CONFIG_STATUS,
  CONFIG_STATUS_LABEL,
  buildTriggerFilterFields,
  readListState,
  writeListState,
  needsVerification,
  isShowingDeleted,
  guidelineText,
} from "../../utils";
import PageLoadingSpinner from "../../components/PageLoadingSpinner";

const DEFAULT_VIEWS = [
  { _id: "ALL", name: "Toutes", filters: [] },
  { _id: "NEW", name: "Nouvelles", filters: [{ id: "default-new", field: "syncStatus", operator: SEARCH_OPERATOR.IS, values: ["CREE"] }] },
  { _id: "UPDATED", name: "Mises à jour", filters: [{ id: "default-updated", field: "syncStatus", operator: SEARCH_OPERATOR.IS, values: ["MIS_A_JOUR", "CONFLIT"] }] },
  { _id: "MISSING", name: "Disparues", filters: [{ id: "default-missing", field: "syncStatus", operator: SEARCH_OPERATOR.IS, values: ["DELETED"] }] },
];

const STORAGE_KEY = "guideline-list-state";

const Guidelines = () => {
  const history = useHistory();
  const queryClient = useQueryClient();
  const savedState = useMemo(() => readListState(STORAGE_KEY), []);
  const [search, setSearch] = useState(savedState?.search || "");
  const [showDeleted, setShowDeleted] = useState(isShowingDeleted(savedState?.filters));

  const {
    data: guidelines = [],
    isLoading: isLoadingGuidelines,
    isError: isErrorGuidelines,
  } = useQuery({
    queryKey: ["guidelines", { showDeleted }],
    queryFn: async () => {
      const response = await API.get(`/guideline?includeDeleted=${showDeleted}`);
      if (!response.ok) {
        const error = new Error(response.message);
        capture(error);
        throw error;
      }
      return response.data;
    },
  });

  const { data: allOverrides = [] } = useQuery({
    queryKey: ["sync-overrides"],
    queryFn: async () => {
      const response = await API.get("/sync/override");
      if (!response.ok) throw new Error(response.message);
      return response.data;
    },
  });
  const conflictDocIds = useMemo(() => new Set(allOverrides.filter((o) => o.conflict).map((o) => String(o.docId))), [allOverrides]);
  const overrideDocIds = useMemo(() => new Set(allOverrides.map((o) => String(o.docId))), [allOverrides]);

  const { data: sharingNetwork } = useQuery({
    queryKey: ["network-config"],
    queryFn: async () => {
      const response = await API.get("/network-config");
      if (!response.ok) throw new Error(response.message);
      return response.data;
    },
    staleTime: Infinity,
  });

  const filterFields = useMemo(() => {
    const uniqueAudiences = [...new Set(guidelines.flatMap((g) => [g.secretary && "secretary", g.patient && "patient", g.ide && "ide"].filter(Boolean)))].sort();
    const uniqueSyncSources = [...new Set(guidelines.flatMap((g) => g._sources?.map((s) => s.source)).filter(Boolean))].sort();
    const triggerFilters = buildTriggerFilterFields(guidelines, [SEARCH_OPERATOR.IS, SEARCH_OPERATOR.IS_NOT]);

    return [
      {
        key: "audience",
        label: "Audience",
        operators: [SEARCH_OPERATOR.IS, SEARCH_OPERATOR.IS_NOT],
        values: uniqueAudiences,
        resolve: (guideline) => [guideline.secretary && "secretary", guideline.patient && "patient", guideline.ide && "ide"].filter(Boolean),
      },
      ...triggerFilters,
      {
        key: "syncStatus",
        label: "Status synchronisation",
        operators: [SEARCH_OPERATOR.IS, SEARCH_OPERATOR.IS_NOT],
        values: ["CONFLIT", "CREE", "MIS_A_JOUR", "MODIFIE", "DELETED", "SYNCED"],
        resolve: (guideline) => {
          const statuses = [];
          if (conflictDocIds.has(String(guideline._id))) statuses.push("CONFLIT");
          if (guideline.configStatus === CONFIG_STATUS.CREATED) statuses.push("CREE");
          if (guideline.configStatus === CONFIG_STATUS.UPDATED) statuses.push("MIS_A_JOUR");
          if (overrideDocIds.has(String(guideline._id))) statuses.push("MODIFIE");
          if (guideline._sources?.some((s) => s.missingFromSource)) statuses.push("DELETED");
          if (statuses.length === 0 && guideline._sources?.length) statuses.push("SYNCED");
          return statuses;
        },
      },
      {
        key: "syncSource",
        label: "Source synchronisation",
        operators: [SEARCH_OPERATOR.IS, SEARCH_OPERATOR.IS_NOT],
        values: uniqueSyncSources,
        resolve: (guideline) => guideline._sources?.map((s) => s.source).filter(Boolean) || [],
      },
      {
        key: "deleted",
        label: "Supprimée",
        operators: [SEARCH_OPERATOR.IS],
        values: ["Oui", "Non"],
        resolve: (guideline) => (guideline.isDeleted ? "Oui" : "Non"),
      },
      ...(sharingNetwork
        ? [
            {
              key: "network",
              label: "Réseau",
              operators: [SEARCH_OPERATOR.IS, SEARCH_OPERATOR.IS_NOT],
              values: ["PROPRIETAIRE", "RECU", "AUCUN"],
              valueLabels: { PROPRIETAIRE: "Partagée (propriétaire)", RECU: "Reçue du réseau", AUCUN: "Non partagée" },
              resolve: (guideline) => (guideline.sharedToNetwork ? "PROPRIETAIRE" : guideline.networkSourceId ? "RECU" : "AUCUN"),
            },
          ]
        : []),
    ];
  }, [guidelines, conflictDocIds, overrideDocIds, sharingNetwork]);

  const { filters: activeFilters, setFilters: setActiveFilters, applyFilters } = useSearchFilters(filterFields, { initialFilters: savedState?.filters });

  const onFiltersChange = (nextFilters) => {
    setActiveFilters(nextFilters);
    setShowDeleted(isShowingDeleted(nextFilters));
    resetSelection();
  };

  const { savedViews, activeViewId, saveModalOpen, setSaveModalOpen, handleSelectView, isViewActive, handleConfirmSaveView, handleDeleteView, saveOptions } = useSavedViews({
    collectionName: "guideline",
    defaultViews: DEFAULT_VIEWS,
    activeFilters,
    setActiveFilters: onFiltersChange,
    initialViewId: savedState?.viewId,
  });

  useEffect(() => {
    writeListState(STORAGE_KEY, { search, filters: activeFilters, viewId: activeViewId });
  }, [search, activeFilters, activeViewId]);

  const filterCounts = useMemo(() => {
    const created = guidelines.filter((doc) => doc.configStatus === CONFIG_STATUS.CREATED).length;
    const updated = guidelines.filter((doc) => doc.configStatus === CONFIG_STATUS.UPDATED || conflictDocIds.has(String(doc._id))).length;
    const missing = guidelines.filter((doc) => doc._sources?.some((s) => s.missingFromSource)).length;
    return { all: guidelines.length, created, updated, missing };
  }, [guidelines, conflictDocIds]);

  const getViewBadge = (tab) => {
    if (tab._id === "ALL") return <span className="ml-1.5 text-xs text-gray-400">{filterCounts.all}</span>;
    if (tab._id === "NEW" && filterCounts.created > 0)
      return <span className="ml-1.5 rounded-full bg-orange-100 px-1.5 py-0.5 text-xs text-orange-600">{filterCounts.created}</span>;
    if (tab._id === "UPDATED" && filterCounts.updated > 0)
      return <span className="ml-1.5 rounded-full bg-orange-100 px-1.5 py-0.5 text-xs text-orange-600">{filterCounts.updated}</span>;
    if (tab._id === "MISSING" && filterCounts.missing > 0) return <span className="ml-1.5 text-xs text-gray-400">{filterCounts.missing}</span>;
    return null;
  };

  const filteredGuidelines = useMemo(() => {
    const byPriority = [...applyFilters(guidelines)].sort((a, b) => (b.priority || 0) - (a.priority || 0));
    return rankBySearch(byPriority, search, (g) => [g.secretaryGuideline, g.patientGuideline, g.ideGuideline, summarizeGuidelineTriggers(g)].filter(Boolean).join(" "));
  }, [guidelines, search, activeFilters, conflictDocIds, overrideDocIds]);

  const orderedIds = useMemo(() => filteredGuidelines.map((g) => g._id), [filteredGuidelines]);
  const selection = useSelection(orderedIds);
  const selectedNeedsVerification = useMemo(() => guidelines.some((g) => selection.selected.has(g._id) && needsVerification(g)), [guidelines, selection.selected]);
  const selectionHasDeleted = useMemo(() => guidelines.some((g) => selection.selected.has(g._id) && g.isDeleted), [guidelines, selection.selected]);
  const selectedShareableIds = useMemo(
    () => guidelines.filter((g) => selection.selected.has(g._id) && !g.isDeleted && !g.sharedToNetwork && !g.networkSourceId).map((g) => g._id),
    [guidelines, selection.selected],
  );

  const bulkMutation = useMutation({
    mutationFn: ({ action }) => API.post("/guideline/bulk", { ids: Array.from(selection.selected), action }),
    onSuccess: (response, variables) => {
      if (!response.ok) return toast.error(response.message || "Une erreur est survenue");
      const { processed = 0, skipped = 0 } = response.data || {};
      const past = { delete: "supprimée(s)", verify: "vérifiée(s)" }[variables.action];
      let message = `${processed} recommandation(s) ${past}`;
      if (skipped > 0) message += ` — ${skipped} ignorée(s)`;
      toast.success(message);
      queryClient.invalidateQueries({ queryKey: ["guidelines"] });
      queryClient.invalidateQueries({ queryKey: ["diagramData"] });
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      queryClient.invalidateQueries({ queryKey: ["taskCount"] });
      selection.clear();
    },
    onError: (error) => {
      capture(error);
      toast.error("Une erreur est survenue");
    },
  });

  const shareMutation = useMutation({
    mutationFn: () => API.post("/network-config/guideline", { fromIds: selectedShareableIds }),
    onSuccess: (response) => {
      if (!response.ok) return toast.error(response.message || "Une erreur est survenue");
      const { processed = 0, skipped = 0, errors = [] } = response.data || {};
      if (processed === 0 && errors.length > 0) return toast.error(errors[0].message);
      let message = `${processed} recommandation(s) partagée(s) au réseau`;
      if (skipped > 0) message += ` — ${skipped} ignorée(s)`;
      toast.success(message);
      queryClient.invalidateQueries({ queryKey: ["guidelines"] });
      queryClient.invalidateQueries({ queryKey: ["diagramData"] });
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      queryClient.invalidateQueries({ queryKey: ["taskCount"] });
      selection.clear();
    },
    onError: (error) => {
      capture(error);
      toast.error("Une erreur est survenue");
    },
  });

  const listRef = useRef(null);
  const selectedIndexRef = useRef(savedState?.selectedIndex || 0);
  const [selectedIndex, setSelectedIndex] = useState(savedState?.selectedIndex || 0);

  const virtualizer = useVirtualizer({
    count: filteredGuidelines.length,
    getScrollElement: () => listRef.current,
    initialOffset: savedState?.scrollTop || 0,
    estimateSize: () => 53,
    overscan: 5,
  });

  const virtualItems = virtualizer.getVirtualItems();
  const totalHeight = virtualizer.getTotalSize();

  const resetSelection = () => {
    selectedIndexRef.current = 0;
    setSelectedIndex(0);
    virtualizer.scrollToIndex(0);
    writeListState(STORAGE_KEY, { scrollTop: 0, selectedIndex: 0 });
  };

  const navigateToGuideline = (item) => {
    const ids = filteredGuidelines.map((g) => g._id);
    writeListState(STORAGE_KEY, { scrollTop: listRef.current.scrollTop, selectedIndex: ids.indexOf(item._id) });
    history.push({ pathname: `/guideline/${item._id}/edit`, state: { filteredIds: ids } });
  };

  const handleNavigateDown = () => {
    const next = selectedIndexRef.current >= filteredGuidelines.length - 1 ? 0 : selectedIndexRef.current + 1;
    selectedIndexRef.current = next;
    setSelectedIndex(next);
    virtualizer.scrollToIndex(next);
  };

  const handleNavigateUp = () => {
    const prev = selectedIndexRef.current <= 0 ? filteredGuidelines.length - 1 : selectedIndexRef.current - 1;
    selectedIndexRef.current = prev;
    setSelectedIndex(prev);
    virtualizer.scrollToIndex(prev);
  };

  const onRowMouseMove = (event, index) => {
    const pointerDidNotMove = event.movementX === 0 && event.movementY === 0;
    if (pointerDidNotMove || selectedIndexRef.current === index) return;
    selectedIndexRef.current = index;
    setSelectedIndex(index);
  };

  useListShortcuts(
    {
      onUp: handleNavigateUp,
      onDown: handleNavigateDown,
      onOpen: ({ newTab } = {}) => {
        const guideline = filteredGuidelines[selectedIndexRef.current];
        if (!guideline) return;
        if (newTab) window.open(`/guideline/${guideline._id}/edit`, "_blank");
        else navigateToGuideline(guideline);
      },
    },
    [filteredGuidelines, virtualizer],
  );

  useHotkeys(SHORTCUTS.CREATE.keys, () => history.push("/guideline/add"), { preventDefault: true });
  useHotkeys("x", () => selection.select(selectedIndexRef.current), { preventDefault: true, enabled: filteredGuidelines.length > 0 }, [filteredGuidelines.length]);
  useHotkeys(
    "mod+a",
    (e) => {
      e.preventDefault();
      selection.selectAll();
    },
    { preventDefault: true },
    [orderedIds],
  );
  useHotkeys("escape", () => selection.clear(), { enabled: selection.count > 0 }, [selection.count]);

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    resetSelection();
  };

  const getPriorityColor = (priority) => {
    if (!priority) return "bg-gray-100 text-gray-500";
    if (priority >= 80) return "bg-red-50 text-red-700";
    if (priority >= 50) return "bg-amber-50 text-amber-700";
    return "bg-green-50 text-green-700";
  };

  if (isLoadingGuidelines) {
    return <PageLoadingSpinner />;
  }

  if (isErrorGuidelines) {
    return (
      <div className="flex min-h-[calc(100vh-60px)] flex-col items-center justify-center gap-3 bg-gray-50">
        <span className="text-sm text-red-500">Erreur lors du chargement des recommandations</span>
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-60px)] bg-gray-50 p-6">
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-6">
          <h1 className="text-xl font-semibold text-gray-900">Recommandations</h1>
          <ViewTabs
            defaultViews={DEFAULT_VIEWS}
            savedViews={savedViews}
            isViewActive={isViewActive}
            onSelectView={handleSelectView}
            onDeleteView={handleDeleteView}
            getViewBadge={getViewBadge}
          />
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="rounded-md p-1.5 text-gray-500 transition-colors hover:bg-gray-100"
            onClick={() => window.open("/audit-log/guidelines", "_blank", "noopener,noreferrer,width=1000,height=800")}
            title="Journal d'audit"
          >
            <PiClockCounterClockwise className="h-5 w-5" />
          </button>
          <button
            type="button"
            className="flex items-center gap-2 rounded-lg bg-primary-500 px-3 py-1.5 text-sm text-white transition-colors hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
            onClick={() => {
              history.push("/guideline/add");
              notifyShortcut("CREATE");
            }}
          >
            <HiOutlinePlus className="h-4 w-4" />
            <span>Nouvelle recommandation</span>
          </button>
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        <SearchFilters
          fields={filterFields}
          filters={activeFilters}
          onFiltersChange={onFiltersChange}
          search={search}
          onSearchChange={handleSearchChange}
          placeholder="Rechercher une recommandation..."
          saveOptions={saveOptions}
        />

        <div ref={listRef} className="max-h-[calc(100vh-252px)] overflow-y-auto">
          {filteredGuidelines.length > 0 && (
            <div style={{ height: totalHeight, position: "relative" }}>
              {virtualItems.map((virtualRow) => {
                const guideline = filteredGuidelines[virtualRow.index];
                const isSelected = selection.isSelected(guideline._id);
                const isMissing = !guideline.isDeleted && guideline._sources?.some((s) => s.missingFromSource);
                const isConflict = !guideline.isDeleted && conflictDocIds.has(String(guideline._id));
                const hasStateChip = isMissing || isConflict || needsVerification(guideline);
                return (
                  <div
                    key={guideline._id}
                    data-active={virtualRow.index === selectedIndex || undefined}
                    onMouseMove={(event) => onRowMouseMove(event, virtualRow.index)}
                    data-selected={isSelected || undefined}
                    style={{ position: "absolute", top: 0, left: 0, right: 0, height: `${virtualRow.size}px`, transform: `translateY(${virtualRow.start}px)` }}
                    className="group flex cursor-pointer items-center border-b border-gray-100 pr-4 hover:bg-gray-50 data-[active=true]:bg-primary-50/50 data-[selected=true]:bg-primary-50"
                    onClick={() => navigateToGuideline(guideline)}
                  >
                    <button
                      type="button"
                      role="checkbox"
                      aria-checked={isSelected}
                      aria-label="Sélectionner la recommandation"
                      onClick={(e) => {
                        e.stopPropagation();
                        selection.select(virtualRow.index, { shiftKey: e.shiftKey });
                      }}
                      className="group/check flex shrink-0 items-center self-stretch pl-4 pr-3"
                    >
                      <span
                        className={`flex h-[18px] w-[18px] items-center justify-center rounded-md border transition-all ${
                          isSelected ? "border-primary-500 bg-primary-500 text-white shadow-sm" : "border-gray-300 bg-white text-transparent group-hover/check:border-primary-400"
                        } ${selection.count > 0 || isSelected ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`}
                      >
                        <PiCheckBold className="h-2.5 w-2.5" />
                      </span>
                    </button>
                    <span
                      className={`mr-3 inline-flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-xs font-semibold ${getPriorityColor(guideline.priority)}`}
                    >
                      {guideline.priority || "—"}
                    </span>
                    <span className="min-w-0 truncate text-sm font-medium text-gray-900">{guidelineText(guideline)}</span>

                    <div className="ml-auto flex flex-shrink-0 items-center gap-4 pl-4">
                      <div className="flex items-center gap-1.5">
                        {guideline.isDeleted && (
                          <span className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
                            <HiOutlineTrash className="h-3 w-3" />
                            Supprimée
                          </span>
                        )}
                        {!guideline.isDeleted && needsVerification(guideline) && (
                          <span className="inline-flex items-center gap-1 rounded-md border border-orange-200 bg-orange-100 px-2 py-0.5 text-xs font-medium text-orange-700">
                            {guideline.configStatus === CONFIG_STATUS.CREATED ? <HiOutlineSparkles className="h-3 w-3" /> : <HiOutlineRefresh className="h-3 w-3" />}
                            {CONFIG_STATUS_LABEL[guideline.configStatus]}
                          </span>
                        )}
                        {isConflict && (
                          <span className="inline-flex items-center gap-1 rounded-md border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700">
                            <HiOutlineExclamation className="h-3 w-3" />
                            Conflit
                          </span>
                        )}
                        {isMissing && (
                          <span className="inline-flex items-center gap-1 rounded-md border border-red-200 bg-red-50 px-2 py-0.5 text-xs font-medium text-red-700">
                            <HiOutlineTrash className="h-3 w-3" />
                            Supprime
                          </span>
                        )}
                        {!guideline.isDeleted && (guideline.networkSourceId || guideline.sharedToNetwork) && (
                          <span className="inline-flex items-center gap-1 rounded-md border border-primary-200 bg-primary-50 px-2 py-0.5 text-xs font-medium text-primary-700">
                            <LuNetwork className="h-3 w-3" />
                            Réseau
                          </span>
                        )}
                        {!guideline.isDeleted && !hasStateChip && guideline._sources?.length > 0 && (
                          <span className="inline-flex items-center gap-1 rounded-md border border-gray-200 bg-gray-50 px-2 py-0.5 text-xs font-medium text-gray-600">
                            <HiOutlineCloudDownload className="h-3 w-3 text-primary-500" />
                            Sync
                          </span>
                        )}
                      </div>
                      <span className="hidden max-w-[200px] truncate text-xs text-gray-500 lg:block">{summarizeGuidelineTriggers(guideline)}</span>
                      <HiOutlineChevronRight className="h-4 w-4 text-gray-300" />
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {filteredGuidelines.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16 text-gray-400">
              <p className="text-sm">{search || activeFilters.some((f) => f.values.length > 0) ? "Aucun resultat pour les filtres appliques" : "Aucune recommandation trouvée"}</p>
              {!search && activeFilters.length === 0 && <p className="mt-1 text-xs">Créez-en une en cliquant sur le bouton ci-dessus</p>}
            </div>
          )}
        </div>
      </div>

      <BulkActionBar
        count={selection.count}
        total={filteredGuidelines.length}
        noun="recommandation"
        feminine
        loading={bulkMutation.isPending || shareMutation.isPending}
        onClear={selection.clear}
        actions={[
          {
            key: "share",
            label: "Partager au réseau",
            hidden: !sharingNetwork || selectedShareableIds.length === 0,
            onClick: () => shareMutation.mutate(),
          },
          {
            key: "verify",
            label: "Marquer vérifié",
            variant: "primary",
            hidden: !selectedNeedsVerification || selectionHasDeleted,
            onClick: () => bulkMutation.mutate({ action: "verify" }),
          },
          {
            key: "delete",
            label: "Supprimer",
            variant: "danger",
            hidden: selectionHasDeleted,
            confirm: {
              title: `Supprimer ${selection.count} recommandation(s) ?`,
              body: "Cette action est irréversible.",
              confirmLabel: "Supprimer",
            },
            onClick: () => bulkMutation.mutate({ action: "delete" }),
          },
        ]}
      />

      {saveModalOpen && (
        <SaveViewModal
          onClose={() => setSaveModalOpen(false)}
          onConfirm={(name) => {
            setSaveModalOpen(false);
            handleConfirmSaveView(name);
          }}
        />
      )}
    </div>
  );
};

export default Guidelines;
